import {
    AudioSearchResponse,
    WebSearchApiSearchAudioRequest,
    GetSimilarAudioItems200Response,
    StockItem,
    SearchAudioMediaTypeEnum,
} from 'search-gateway-client';
import { v7 } from 'uuid';

import { AudioContentType } from '@videoblocks/events-ts/lib/storyblocks/search/AudioContentTypeEnum';
import { QueryMode } from '@videoblocks/events-ts/lib/storyblocks/search/QueryModeEnum';
import {
    type Pagination,
    SearchResponse,
    SearchRequest,
    SearchResult,
} from '@videoblocks/events-ts/lib/storyblocks/search/SearchEvent';
import { SearchOrigin } from '@videoblocks/events-ts/lib/storyblocks/search/SearchOriginEnum';
import { SortType } from '@videoblocks/events-ts/lib/storyblocks/search/SortTypeEnum';
import { VocalType } from '@videoblocks/events-ts/lib/storyblocks/search/VocalTypeEnum';

import { type SearchEventData } from './SearchEventData';

export type Ranker = (item: StockItem, index: number) => number;

export function mapAudioToSearchResponse(
    src: AudioSearchResponse,
    opts: {
        pagination: Pagination;
        ranker: Ranker;
    },
): SearchResponse {
    const { pagination, ranker } = opts;

    const results: SearchResult[] = (src.stockItems ?? []).map((item, idx) => ({
        stockItemId: item.stockblocksId,
        contentId: item.id,
        collapsedSetId: item.collapsedSetId,
        numCollapsed: item.numCollapses,
        rank: ranker(item, idx),
        // Not present in source;
        injectionType: undefined,
        gtacOrderComputationMethod: undefined,
        isBipoc: undefined,
    }));

    return {
        pagination,
        results,
        durationMs: src.durationMs ?? null,
        similarToStockItemId: null,
        statusCode: 200,
        cacheHit: null,
        // `suggestions` in SearchResponse != `relatedSearches` in AudioSearchResponse (different shape)
        suggestions: null,
        algorithmApplied: src.algorithmApplied ?? '',
        injectionsApplied: src.injectionsApplied ?? [],
    };
}

export function mapRelatedAudioToSearchResponse(
    src: GetSimilarAudioItems200Response,
    opts: {
        pagination: Pagination;
        ranker: Ranker;
    },
    contentId: string, // Added contentId parameter to match the function signature
): SearchResponse {
    const { pagination, ranker } = opts;

    const results: SearchResult[] = (src.similarItems ?? []).map((item, idx) => ({
        stockItemId: item.stockblocksId,
        contentId: item.id,
        collapsedSetId: item.collapsedSetId,
        numCollapsed: item.numCollapses,
        rank: ranker(item, idx),
        // Not present in source;
        injectionType: undefined,
        gtacOrderComputationMethod: undefined,
        isBipoc: undefined,
    }));

    return {
        pagination,
        results,
        durationMs: null,
        similarToStockItemId: contentId ? parseInt(contentId, 10) : null,
        statusCode: 200,
        cacheHit: null,
        // `suggestions` in SearchResponse != `relatedSearches` in AudioSearchResponse (different shape)
        suggestions: null,
        algorithmApplied: '',
        injectionsApplied: [],
    };
}

function mapSortBy(
    sortBy?:
        | 'title'
        | 'most_relevant'
        | 'most_downloaded'
        | 'most_recent'
        | 'trending_now'
        | 'undiscovered'
        | 'duration',
): SortType {
    switch (sortBy) {
        case 'most_relevant':
            return SortType.MOST_RELEVANT;
        case 'most_downloaded':
            return SortType.MOST_DOWNLOADED;
        case 'most_recent':
            return SortType.MOST_RECENT;
        case 'trending_now':
            return SortType.TRENDING_NOW;
        case 'undiscovered':
            return SortType.UNDISCOVERED;
        case 'duration':
            return SortType.DURATION;
        case 'title':
            return SortType.TITLE;
        default:
            return SortType.MOST_RELEVANT;
    }
}

function mapAudioContentType(
    mediaType?: (typeof SearchAudioMediaTypeEnum)[keyof typeof SearchAudioMediaTypeEnum],
): AudioContentType {
    if (mediaType === 'Music') return AudioContentType.MUSIC;
    if (mediaType === 'SFX') return AudioContentType.SOUND_EFFECTS;
    return AudioContentType.ALL;
}

function mapVocalType(hasVocals?: boolean): VocalType {
    if (hasVocals === true) return VocalType.HAS_VOCALS;
    if (hasVocals === false) return VocalType.INSTRUMENTAL;
    return VocalType.ALL;
}

export function mapAudioWebRequestToSearchRequest(
    src: WebSearchApiSearchAudioRequest,
    searchOrigin: string | undefined,
): SearchRequest {
    const searchOriginMapped = searchOrigin
        ? SearchOrigin[searchOrigin.toUpperCase() as keyof typeof SearchOrigin]
        : null;

    return {
        keywords: src.searchTerm ?? '',
        excludeKeywords: null,
        libraryIds: [],
        categoryIds: Array.isArray(src.sfxCategories) ? src.sfxCategories : [],
        categoryIdsComplex: null,
        // no contributors in input
        contributorIds: [],
        artistIds: Array.isArray(src.artistId) ? src.artistId.map(Number) : [],
        similarToId: null,
        searchOrigin: searchOriginMapped,
        mode: QueryMode.FULLTEXT,
        sortType: mapSortBy(src.sortBy),
        videoProps: null,
        imageProps: null,

        audioProps: {
            contentType: mapAudioContentType(src.mediaType),
            minDuration: src.minDuration ?? null,
            maxDuration: src.maxDuration ?? null,
            minBpm: src.bpmMin ?? null,
            maxBpm: src.bpmMax ?? null,
            genreIds: src.musicGenres ?? [],
            moodIds: src.musicMoods ?? [],
            instrumentIds: src.musicInstruments ?? [],
            includeVbContentOnly: false,
            vocalType: mapVocalType(src.hasVocals),
        },
        algorithm: src.algorithm ?? '',
        collapsedSetId: src.collapsedSetId ?? null,
        format: [],
    };
}

export function mapRelatedAudioWebRequestToSearchRequest(
    src: WebSearchApiSearchAudioRequest,
    searchOrigin: string | undefined,
    contentId: string,
): SearchRequest {
    const searchOriginMapped = searchOrigin
        ? SearchOrigin[searchOrigin.toUpperCase() as keyof typeof SearchOrigin]
        : null;

    return {
        keywords: '',
        excludeKeywords: null,
        libraryIds: [],
        categoryIds: [],
        categoryIdsComplex: null,
        contributorIds: [],
        artistIds: Array.isArray(src.artistId) ? src.artistId.map(Number) : [],
        similarToId: contentId ? parseInt(contentId, 10) : null,
        searchOrigin: searchOriginMapped,
        mode: QueryMode.FULLTEXT,
        sortType: mapSortBy(src.sortBy),
        videoProps: null,
        imageProps: null,

        audioProps: {
            contentType: mapAudioContentType(src.mediaType),
            minDuration: src.minDuration ?? null,
            maxDuration: src.maxDuration ?? null,
            minBpm: src.bpmMin ?? null,
            maxBpm: src.bpmMax ?? null,
            genreIds: src.musicGenres ?? [],
            moodIds: src.musicMoods ?? [],
            instrumentIds: src.musicInstruments ?? [],
            includeVbContentOnly: false,
            vocalType: mapVocalType(src.hasVocals),
        },
        algorithm: src.algorithm ?? '',
        collapsedSetId: src.collapsedSetId ?? null,
        format: [],
    };
}

export function buildAudioSearchEventData(
    searchData: AudioSearchResponse | null | undefined,
    searchRequest: WebSearchApiSearchAudioRequest,
    searchOrigin: string | undefined,
): SearchEventData {
    if (!searchData) {
        return {
            searchRequest: mapAudioWebRequestToSearchRequest(searchRequest, searchOrigin),
            searchResponse: {
                pagination: {
                    pageNum: 1,
                    numResultsPerPage: 0,
                    totalResults: 0,
                    nextPageUrl: null,
                    prevPageUrl: null,
                },
                results: [],
                durationMs: null,
                similarToStockItemId: null,
                statusCode: 200,
                cacheHit: null,
                suggestions: null,
                algorithmApplied: '',
                injectionsApplied: [],
            },
            searchIdentifiers: {
                searchId: v7(),
                searchPageId: v7(),
            },
        };
    }
    const pagination: Pagination = {
        pageNum: searchRequest.pageNumber ?? 1,
        numResultsPerPage: searchData.stockItems.length,
        totalResults: searchData.totalCount,
        nextPageUrl: null,
        prevPageUrl: null,
    };

    const ranker: Ranker = (_item, index) => index + 1;

    const searchResponse: SearchResponse = mapAudioToSearchResponse(searchData, { pagination, ranker });
    const searchRequestMapped = mapAudioWebRequestToSearchRequest(searchRequest, searchOrigin);

    return {
        searchRequest: searchRequestMapped,
        searchResponse,
        searchIdentifiers: {
            searchId: v7(),
            searchPageId: v7(),
        },
    };
}

export function buildRelatedAudioSearchEventData(
    searchData: GetSimilarAudioItems200Response,
    searchRequest: WebSearchApiSearchAudioRequest,
    searchOrigin: string | undefined,
    contentId: string,
): SearchEventData {
    const pagination: Pagination = {
        pageNum: searchRequest.pageNumber ?? 1,
        numResultsPerPage: searchData.similarItems.length,
        totalResults: searchData.totalCount,
        nextPageUrl: null,
        prevPageUrl: null,
    };

    const ranker: Ranker = (_item, index) => index + 1;

    const searchResponse: SearchResponse = mapRelatedAudioToSearchResponse(
        searchData,
        { pagination, ranker },
        contentId,
    );
    const searchRequestMapped = mapRelatedAudioWebRequestToSearchRequest(searchRequest, searchOrigin, contentId);

    return {
        searchRequest: searchRequestMapped,
        searchResponse,
        searchIdentifiers: {
            searchId: v7(),
            searchPageId: v7(),
        },
    };
}
